/**
 * reportWinners.js — who won each GAM request, for the sign-off report's
 * "Requests graded" table. Reuses the Slots view's Winner step (buildSlotStory)
 * so the report and the dashboard never disagree: Liftoff and other SDK-bidding
 * buyers (billing notice / adapter label), the GAM-named adapter (e.g. AdX),
 * the Prebid bidder whose hb_bidder GAM was sent, no fill, or not visible on a
 * USB capture. Pure apart from the injected loaders; never throws.
 */
import { buildSlotStory } from './slotStory';

const keyOf = (r) => [r?._k, r?.id, r?.requestId].filter((v) => v !== undefined && v !== null && v !== '').map(String);

export function winnerOf(lc) {
  try {
    const story = buildSlotStory(lc);
    const step = (story?.steps || []).find((s) => s && s.id === 'winner');
    if (!step) return null;
    const why = Array.isArray(step.why) ? step.why : [];
    let label;
    if (step.state === 'ok') label = step.summary || 'Winner identified';
    else if (step.state === 'unknown') label = 'Not visible (USB capture)';
    else if (why.some((w) => /no fill/i.test(w))) label = 'No fill';
    else label = 'Not identified';
    return { state: step.state, label, detail: why[0] || '' };
  } catch {
    return null;
  }
}

/** Map every key of a slot's GAM row (_k / id / requestId) → winnerOf(that slot). */
export function winnersByGamRow(lifecycles) {
  const out = new Map();
  for (const lc of Array.isArray(lifecycles) ? lifecycles : []) {
    const gam = lc?.phases?.gam;
    if (!gam) continue;
    const w = winnerOf(lc);
    if (!w) continue;
    const keys = new Set([...(gam.rowKey != null ? [String(gam.rowKey)] : []), ...keyOf(gam.row)]);
    for (const k of keys) if (!out.has(k)) out.set(k, w);
  }
  return out;
}

/**
 * loadReportLifecycles(rows, { post, buildLocal }) → Promise<lifecycles[]>.
 * One build per device (impression/bill matching is by timing; mixing phones
 * cross-attributes), proxy answer first, local builder on failure.
 */
export async function loadReportLifecycles(rows, { post, buildLocal } = {}) {
  const byDevice = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    const d = (r && (r._deviceTab || r.deviceId)) || '';
    if (!byDevice.has(d)) byDevice.set(d, []);
    byDevice.get(d).push(r);
  }
  const parts = await Promise.all([...byDevice.values()].map(async (list) => {
    try {
      const g = await post(list);
      if (Array.isArray(g?.lifecycles)) return g.lifecycles;
    } catch { /* fall back to the local twin */ }
    try { return buildLocal(list) || []; } catch { return []; }
  }));
  return parts.flat();
}
